import React, { useEffect, useRef } from 'react';

import { ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';

const AMAZON_PRODUCT_URL = 'https://www.amazon.com/s/ref=nb_sb_ss_i_1_0?url=search-alias%3Daps&field-keywords=bread+proofing+basket+set&field-asin=B0G29RQGT9&rh=i:aps,ssx:relevance';

const HomeKitVideoSection = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Autoplay can be blocked unless the element is muted AND play is triggered
    // after it's in the viewport. This ensures the intended behavior across browsers.
    const tryPlay = () => {
      video.muted = true;
      // Best-effort autoplay (some browsers still block; user can press play)
      video.play().catch(() => undefined);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          tryPlay();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
    video.addEventListener('canplay', tryPlay);

    return () => {
      observer.disconnect();
      video.removeEventListener('canplay', tryPlay);
    };
  }, []);

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1919] mb-4">
              Watch your kit in action
            </h2>
            <p className="text-base md:text-lg text-[#736c64] leading-relaxed mb-6">
              A quick walkthrough of what’s included—and how each tool helps you bake with more ease and confidence.
            </p>
            <Button asChild className="bg-[#a8b5a0] hover:bg-[#8fa087] text-white">
              <a href={AMAZON_PRODUCT_URL} target="_blank" rel="noreferrer">
                View on Amazon
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
            <p className="text-xs text-[#736c64] mt-3">
              Already have your kit? Start with “Getting Started” for step-by-step help.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-xl border border-black/5 bg-[#f7f5f2]">
            <video
              ref={videoRef}
              className="w-full h-auto"
              poster="/media/hearthlowe-kit-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
            >
              <source src="/media/hearthlowe-kit-in-action.webm" type="video/webm" />
              <source src="/media/hearthlowe-kit-in-action.mp4" type="video/mp4" />
              Your browser doesn’t support embedded video. You can still view the kit on Amazon.
            </video>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeKitVideoSection;
